import { useEffect, useState } from "react";
import { Box } from "@mui/material";

/**
 * Ticker avatar — tries the Parqet logo CDN first and falls back to a
 * monogrammed circle with a deterministic hue if the request fails.
 *
 * Why Parqet?
 *  • It hosts a large library of equity/ETF logos keyed by uppercase
 *    ticker symbol, served as `https://assets.parqet.com/logos/symbol/{TICKER}`
 *    with a permissive CORS header.
 *  • We don't proxy or cache — the browser does that for us via HTTP
 *    caching headers. Parqet responds with `immutable` + a long max-age
 *    for recognised tickers, so repeat renders are free.
 *  • For the handful of tickers Parqet doesn't cover (obscure ETFs,
 *    very new listings), the `onError` handler flips to the monogram
 *    fallback, so the row never looks empty.
 *
 * A per-module `Set` records tickers we've already seen fail so that
 * the next render for the same symbol skips the image attempt
 * altogether — avoids the split-second flash of a broken image icon
 * on every expand/collapse cycle.
 */

// Module-local cache of symbols that Parqet doesn't have an image for.
// Lives for the length of the page session, which is the right horizon
// — a page reload gives Parqet another chance in case they've added it.
const FAILED_SYMBOLS = new Set<string>();

interface TickerAvatarProps {
  /** Uppercase ticker symbol; lowercase works too — we uppercase for the URL. */
  symbol: string;
  /** Diameter in px. Defaults to 36 which matches the orders list row. */
  size?: number;
}

export function TickerAvatar({ symbol, size = 36 }: TickerAvatarProps) {
  const normalized = symbol.toUpperCase();
  const [errored, setErrored] = useState<boolean>(() =>
    FAILED_SYMBOLS.has(normalized)
  );

  // Reset the error flag if the symbol changes — the cached miss for
  // "AAPL" shouldn't carry over when the row re-renders as "MSFT".
  useEffect(() => {
    setErrored(FAILED_SYMBOLS.has(normalized));
  }, [normalized]);

  if (errored) {
    return <MonogramAvatar symbol={normalized} size={size} />;
  }

  // Parqet returns different image formats (SVG/PNG) depending on the
  // asset — we let the browser pick by hitting the plain endpoint.
  const url = `https://assets.parqet.com/logos/symbol/${encodeURIComponent(normalized)}`;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        // Square with a gentle corner radius — Parqet serves
        // square artwork, so a circular mask was carving off the
        // corners and leaving white fill around the logo. A
        // small radius keeps the avatar feeling "card-like"
        // without hiding any of the logo art.
        borderRadius: 1.25,
        overflow: "hidden",
        // No background fill — the logos are self-contained and
        // paint their own backgrounds. Leaving it transparent
        // means transparent-background logos blend with the row.
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Box
        component="img"
        src={url}
        alt={`${normalized} logo`}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          FAILED_SYMBOLS.add(normalized);
          setErrored(true);
        }}
        sx={{
          width: "100%",
          height: "100%",
          // `cover` (not `contain`) pushes the square artwork
          // out to the edges of the square avatar, eliminating
          // any letterboxing. Parqet art is already centered, so
          // cropping a hair off the edges — if it happens at all
          // — is invisible at 36px.
          objectFit: "cover",
          display: "block"
        }}
      />
    </Box>
  );
}

/**
 * Fallback used when Parqet doesn't have the symbol. Keeps the visual
 * shape stable (same size, same circular footprint) so the layout
 * doesn't jump when one ticker falls back and its neighbours don't.
 */
function MonogramAvatar({ symbol, size }: { symbol: string; size: number }) {
  const hue = hashHue(symbol);
  const label = symbol.slice(0, 2);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        // Matches the square-with-rounded-corners shape of the logo
        // variant so the layout doesn't jump when a ticker falls back
        // to a monogram.
        borderRadius: 1.25,
        bgcolor: `hsl(${hue}, 55%, 42%)`,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.36),
        fontWeight: 700,
        letterSpacing: 0.5
      }}
    >
      {label}
    </Box>
  );
}

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % 360;
}
